
import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { Star, Quote } from "lucide-react";

const New2Testimonials = () => {
  const { language } = useLanguage();
  
  const content = {
    fr: {
      title: "Ce que disent nos familles",
      subtitle: "Plus de 1000 familles nous font confiance pour créer des moments uniques",
      testimonials: [
        {
          name: "Marie et Pierre Dubois",
          location: "Genève, Suisse",
          rating: 5,
          text: "Nous avons créé une capsule pour les 18 ans de notre fille. Voir son émotion en découvrant nos messages d'amour et sa contribution pour ses études était magique. Legatia a transformé un simple investissement en un moment familial inoubliable.",
          avatar: "/assets/testimonial-1.jpg"
        },
        {
          name: "Sophie Martin",
          location: "Lyon, France",
          rating: 5,
          text: "En tant que grand-mère, je voulais être présente pour les grands moments de mes petits-enfants même quand je ne serai plus là. Legatia m'a permis d'enregistrer des messages pour leurs 20 ans. C'est un cadeau d'amour qui traversera le temps.",
          avatar: "/assets/testimonial-2.jpg"
        },
        {
          name: "Famille Schneider",
          location: "Zurich, Suisse",
          rating: 5,
          text: "Nous avons impliqué toute la famille élargie dans la capsule temporelle pour le mariage de notre fils. 15 personnes ont contribué avec des messages et des fonds. Le jour J, l'émotion était indescriptible. Un vrai trésor familial.",
          avatar: "/assets/testimonial-3.jpg"
        }
      ]
    },
    en: {
      title: "What our families say",
      subtitle: "Over 1000 families trust us to create unique moments",
      testimonials: [
        {
          name: "Marie and Pierre Dubois",
          location: "Geneva, Switzerland",
          rating: 5,
          text: "We created a capsule for our daughter's 18th birthday. Seeing her emotion discovering our love messages and her contribution for her studies was magical. Legatia transformed a simple investment into an unforgettable family moment.",
          avatar: "/assets/testimonial-1.jpg"
        },
        {
          name: "Sophie Martin",
          location: "Lyon, France",
          rating: 5,
          text: "As a grandmother, I wanted to be present for my grandchildren's big moments even when I'm no longer here. Legatia allowed me to record messages for their 20th birthday. It's a gift of love that will transcend time.",
          avatar: "/assets/testimonial-2.jpg"
        },
        {
          name: "Schneider Family",
          location: "Zurich, Switzerland",
          rating: 5,
          text: "We involved the entire extended family in the time capsule for our son's wedding. 15 people contributed with messages and funds. On the big day, the emotion was indescribable. A true family treasure.",
          avatar: "/assets/testimonial-3.jpg"
        }
      ]
    },
    de: {
      title: "Was unsere Familien sagen",
      subtitle: "Über 1000 Familien vertrauen uns, einzigartige Momente zu schaffen",
      testimonials: [
        {
          name: "Marie und Pierre Dubois",
          location: "Genf, Schweiz",
          rating: 5,
          text: "Wir haben eine Kapsel für den 18. Geburtstag unserer Tochter erstellt. Ihre Emotion zu sehen, als sie unsere Liebesbotschaften und ihren Beitrag für ihr Studium entdeckte, war magisch. Legatia verwandelte eine einfache Investition in einen unvergesslichen Familienmoment.",
          avatar: "/assets/testimonial-1.jpg"
        },
        {
          name: "Sophie Martin",
          location: "Lyon, Frankreich",
          rating: 5,
          text: "Als Großmutter wollte ich bei den großen Momenten meiner Enkelkinder dabei sein, auch wenn ich nicht mehr da bin. Legatia ermöglichte es mir, Nachrichten für ihren 20. Geburtstag aufzunehmen. Es ist ein Geschenk der Liebe, das die Zeit überdauern wird.",
          avatar: "/assets/testimonial-2.jpg"
        },
        {
          name: "Familie Schneider",
          location: "Zürich, Schweiz",
          rating: 5,
          text: "Wir haben die gesamte erweiterte Familie in die Zeitkapsel für die Hochzeit unseres Sohnes einbezogen. 15 Personen trugen mit Nachrichten und Mitteln bei. Am großen Tag war die Emotion unbeschreiblich. Ein wahrer Familienschatz.",
          avatar: "/assets/testimonial-3.jpg"
        }
      ]
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {content[language].title}
          </h2>
          <p className="text-xl text-gray-600">
            {content[language].subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {content[language].testimonials.map((testimonial, index) => (
            <Card key={index} className="bg-white shadow-lg border-0 hover:shadow-xl transition-all duration-300">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  <Quote className="h-8 w-8 text-legatia-orange mb-4" />
                </div>
                
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                
                <p className="text-gray-700 leading-relaxed mb-6 italic">
                  "{testimonial.text}"
                </p>
                
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-gradient-to-br from-legatia-orange to-legatia-red rounded-full flex items-center justify-center text-white font-semibold mr-4">
                    {testimonial.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.name}</div>
                    <div className="text-sm text-gray-600">{testimonial.location}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default New2Testimonials;
